import React, { useEffect, useMemo, useState } from "react";
import { KeyRound, Plus } from "lucide-react";
import {
  Link,
  Redirect,
  useHistory,
  useLocation,
  useParams,
} from "react-router-dom";
import { Spinner } from "@kui-react/spinner";
import ApplicationPageLayout from "components/ApplicationShell/ApplicationPageLayout";
import { Button } from "components/Graphs/Button";
import { Input } from "components/Graphs/Input";
import { OrgContext } from "contexts/OrgContext";
import { useManagedSecretsQuery } from "server/devplane/managedSecrets/queries";
import { CreateSecretDialog } from "./CreateSecretDialog";
import {
  buildOrganizationSettingsSecretHref,
  getOrganizationSettingsTabRedirectHref,
} from "components/OrgSettings/settingsTabs";

const formatDate = (value?: string) =>
  value
    ? new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(value))
    : "—";

export const ManagedSecretsRedirect: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const { activeOrgId } = React.useContext(OrgContext);

  useEffect(() => {
    if (!activeOrgId) return;

    const redirectHref = getOrganizationSettingsTabRedirectHref({
      activeOrgId,
      tab: "secrets",
      search: location.search,
    });

    if (redirectHref) history.replace(redirectHref);
  }, [activeOrgId, history, location.search]);

  return null;
};

interface ManagedSecretsPageProps {
  embedded?: boolean;
  useSettingsDetailRoutes?: boolean;
}

const ManagedSecretsPage: React.FC<ManagedSecretsPageProps> = ({
  embedded = false,
  useSettingsDetailRoutes = false,
}) => {
  const { orgId } = useParams<{ orgId: string }>();
  const history = useHistory();
  const location = useLocation();
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const secretsQuery = useManagedSecretsQuery(orgId);

  useEffect(() => {
    document.title = "Secrets | Brev";
  }, []);

  const secrets = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch) return secretsQuery.data ?? [];
    return (secretsQuery.data ?? []).filter((secret) =>
      secret.name.toLowerCase().includes(normalizedSearch),
    );
  }, [search, secretsQuery.data]);
  const getSecretHref = (secretId: string) =>
    useSettingsDetailRoutes
      ? buildOrganizationSettingsSecretHref(orgId, secretId, location.search)
      : `/org/${orgId}/secrets/${secretId}`;

  if (!embedded && orgId) {
    const redirectHref = getOrganizationSettingsTabRedirectHref({
      routeOrgId: orgId,
      tab: "secrets",
      search: location.search,
    });

    if (redirectHref) return <Redirect to={redirectHref} />;
  }

  const content = (
    <>
      <CreateSecretDialog
        open={createOpen}
        organizationId={orgId}
        onOpenChange={setCreateOpen}
        onCreated={(secretId) => {
          setCreateOpen(false);
          history.push(getSecretHref(secretId));
        }}
      />
      {!embedded && (
        <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-primary">Secrets</h1>
            <p className="mt-2 text-sm text-secondary">
              Manage versioned secrets in this organization.
            </p>
          </div>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
            Create secret
          </Button>
        </div>
      )}
      {embedded && (
        <div className="flex justify-end">
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
            Create secret
          </Button>
        </div>
      )}

      <div
        className={
          embedded
            ? "mt-5 overflow-hidden rounded-lg border border-zinc-800 bg-secondaryBg"
            : "mt-8 overflow-hidden rounded-lg border border-zinc-800 bg-secondaryBg"
        }
      >
        <div className="flex justify-end border-b border-zinc-800 px-5 py-4">
          <Input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search secrets"
            aria-label="Search secrets"
            className="sm:w-72"
          />
        </div>

        {secretsQuery.isLoading ? (
          <div className="flex min-h-56 items-center justify-center">
            <Spinner size="medium" />
          </div>
        ) : secretsQuery.isError ? (
          <div className="px-5 py-12 text-center">
            <p className="text-sm font-medium text-red-400">
              Unable to load secrets
            </p>
            <p className="mt-1 text-xs text-secondary">
              {secretsQuery.error instanceof Error
                ? secretsQuery.error.message
                : "Try again in a moment."}
            </p>
            <Button
              variant="secondary"
              className="mt-4"
              onClick={() => secretsQuery.refetch()}
            >
              Try again
            </Button>
          </div>
        ) : secrets.length === 0 ? (
          <div className="flex min-h-64 flex-col items-center justify-center px-5 text-center">
            <div className="rounded-full border border-zinc-700 bg-zinc-900 p-3 text-highlight">
              <KeyRound className="h-5 w-5" aria-hidden="true" />
            </div>
            <h2 className="mt-4 text-sm font-medium text-primary">
              {search ? "No matching secrets" : "No secrets yet"}
            </h2>
            {search && (
              <p className="mt-1 max-w-sm text-xs text-secondary">
                Try a different search term.
              </p>
            )}
            {!search && (
              <Button className="mt-4" onClick={() => setCreateOpen(true)}>
                Create secret
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-zinc-800 text-left">
              <thead className="bg-zinc-950/40">
                <tr className="text-xs font-medium uppercase tracking-wide text-secondary">
                  <th className="px-5 py-3">Name</th>
                  <th className="px-5 py-3">Latest version</th>
                  <th className="px-5 py-3">Updated</th>
                  <th className="px-5 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {secrets.map((secret) => (
                  <tr key={secret.id} className="hover:bg-zinc-900/40">
                    <td className="px-5 py-4">
                      <Link
                        to={getSecretHref(secret.id)}
                        className="font-mono text-sm font-medium text-primary hover:text-highlight"
                      >
                        {secret.name}
                      </Link>
                    </td>
                    <td className="px-5 py-4 text-sm text-secondary">
                      v{secret.latestVersionNumber.toString()}
                    </td>
                    <td className="whitespace-nowrap px-5 py-4 text-sm text-secondary">
                      {formatDate(secret.updateTime || secret.createTime)}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <Button
                        asChild
                        variant="secondary"
                        className="h-9 hover:bg-zinc-800"
                      >
                        <Link to={getSecretHref(secret.id)}>Manage</Link>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );

  if (embedded) return <div className="pt-1">{content}</div>;

  return (
    <ApplicationPageLayout className="px-4 py-10 sm:px-6 lg:px-8">
      {content}
    </ApplicationPageLayout>
  );
};

export default ManagedSecretsPage;
